import React, { lazy, Suspense, useEffect } from "react";
import classes from "./mainRoute.module.css";
import Header from "../../components/Header";
import { Route, Routes, useSearchParams } from "react-router-dom";
import useScrollTop from "../../hooks/useScrollTop";
import AboutPage from "../../features/AboutPage";
import { toast } from "react-toastify";
import SubjectFormModal from "../../features/FileDetail/SubjectFormModal";
import LoadingFallBack from "../../components/LoadingFallback";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { loadMasterFromStorage, loadPracticeTimeCountList } from "../../redux/slices/masterSlice";
import { useColorsMasterSelector } from "../../redux/selector";
import LanguageRoute from "../LanguageRoute";
import Authentication from "../../features/Authentication";
import PracticeSubject from "../../features/PracticeSubject";
import PracticeControl from "../../features/PracticeControl";
import PracticeHistory from "../../features/PracticeHistory";
import PracticeReview from "../../features/PracticeReview";
import PracticeHome from "../../features/PracticeHome";
import { unwrapResult } from "@reduxjs/toolkit";

const Home = lazy(() => import("../../features/Home"));
const Footer = lazy(() => import("../../components/Footer"));
const FileDetail = lazy(() => import("../../features/FileDetail"));
const CalcPoint = lazy(
  () => import("../../features/GradeCalculator/gradeCalculator")
);
const NotFound = lazy(() => import("../../features/NotFound"));
const Convert10To4 = lazy(() => import("../../features/Convert10To4"));

function MainRoute() {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const colors = useColorsMasterSelector();
  useScrollTop();

  useEffect(() => {
    const type = searchParams.get("type");
    const message = searchParams.get("message");
    if ((message && type === "info") || type === "error") {
      toast[type](message);
    }
  }, [searchParams]);

  useEffect(() => {
    dispatch(loadMasterFromStorage());
  }, [dispatch]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--primary-color",
      colors.primary
    );
    document.documentElement.style.setProperty(
      "--secondary-color",
      colors.secondary
    );
    document.documentElement.style.setProperty(
      "--tertiary-color",
      colors.tertiary
    );
    document.documentElement.style.setProperty("--info-color", colors.info);
    document.documentElement.style.setProperty(
      "--success-color",
      colors.success
    );
    document.documentElement.style.setProperty(
      "--warning-color",
      colors.warning
    );
    document.documentElement.style.setProperty("--grey-color", colors.grey);
    document.documentElement.style.setProperty(
      "--light-grey-color",
      colors.lightGrey
    );
    document.documentElement.style.setProperty(
      "--dark-grey-color",
      colors.darkGrey
    );
    document.documentElement.style.setProperty("--white", colors.white);
    document.documentElement.style.setProperty("--black", colors.black);
  }, [colors]);

  useEffect(() => {
    dispatch(loadPracticeTimeCountList()).then(unwrapResult);
  }, [])

  return (
    <Suspense fallback={<LoadingFallBack />}>
      <Header />
      <main className={classes.content}>
        <Routes>
          <Route
            path={"/"}
            element={<LanguageRoute />}
            children={[
              <Route path="/" element={<Home />} />,
              <Route path="/gpa-cpa-learning-plan" element={<FileDetail />} />,
              <Route path="/about" element={<AboutPage />} />,
              <Route
                path={"/grade-summary-calculator"}
                element={<CalcPoint />}
              />,
              <Route path={"/convert-10-to-4"} element={<Convert10To4 />} />,
              <Route path="/login" element={<Authentication />} />,
              <Route path="/register" element={<Authentication />} />,
              <Route path={"/practice-home"} element={<PracticeHome />} />,
              <Route
                path={`/practice-subject/:subjectCode`}
                element={<PracticeSubject />}
              />,
              <Route
                path={`/practice-review/:practiceCode`}
                element={<PracticeReview />}
              />,
              <Route
                path={"/practice-control"}
                element={<PracticeControl />}
              />,
              <Route
                path={"/practice-history"}
                element={<PracticeHistory />}
              />,
            ]}
          />
          <Route
            path={"/:languageCode"}
            element={<LanguageRoute />}
            children={[
              <Route path="/:languageCode" element={<Home />} />,
              <Route
                path="/:languageCode/gpa-cpa-learning-plan"
                element={<FileDetail />}
              />,
              <Route path="/:languageCode/about" element={<AboutPage />} />,
              <Route
                path={"/:languageCode/grade-summary-calculator"}
                element={<CalcPoint />}
              />,
              <Route
                path={"/:languageCode/convert-10-to-4"}
                element={<Convert10To4 />}
              />,
              <Route
                path="/:languageCode/login"
                element={<Authentication />}
              />,
              <Route
                path="/:languageCode/register"
                element={<Authentication />}
              />,
              <Route
                path={"/:languageCode/practice-home"}
                element={<PracticeHome />}
              />,
              <Route
                path={`/:languageCode/practice-subject/:subjectCode`}
                element={<PracticeSubject />}
              />,
              <Route
                path={`/:languageCode/practice-review/:practiceCode`}
                element={<PracticeReview />}
              />,
              <Route
                path={"/:languageCode/practice-control"}
                element={<PracticeControl />}
              />,
              <Route
                path={"/:languageCode/practice-history"}
                element={<PracticeHistory />}
              />,
            ]}
          />
          <Route path={"*"} element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <SubjectFormModal />
    </Suspense>
  );
}

export default MainRoute;
